/**
 * Defensive parser for the (unstable, unversioned) instance-monitoring payload.
 * The endpoint is backend-flaky on staging (401 customer-resolve); shape is
 * unknown, so we probe a handful of common layouts and normalise to a flat
 * `{ t, cpu, memory }[]` series for the Chart. Returns [] when nothing usable.
 */

export interface MonitoringPoint {
  t: string;
  cpu: number;
  memory: number;
  /** Index signature so the point is assignable to the Chart's row type. */
  [key: string]: string | number;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 10) / 10 : 0;
}

function label(v: unknown, i: number): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return new Date(v > 1e12 ? v : v * 1000).toLocaleTimeString();
  return `#${i + 1}`;
}

/** Normalise an unknown monitoring blob into a CPU/memory time series. */
export function toMonitoringSeries(raw: Record<string, unknown> | undefined): MonitoringPoint[] {
  if (!raw || typeof raw !== 'object') return [];

  // Preferred: an explicit array under one of the common keys.
  const arr =
    (Array.isArray(raw.series) && raw.series) ||
    (Array.isArray(raw.points) && raw.points) ||
    (Array.isArray(raw.metrics) && raw.metrics) ||
    (Array.isArray(raw.data) && raw.data) ||
    null;

  if (arr) {
    return (arr as Record<string, unknown>[]).map((p, i) => ({
      t: label(p.timestamp ?? p.time ?? p.t ?? p.ts, i),
      cpu: num(p.cpu ?? p.cpu_usage_pct ?? p.cpu_pct),
      memory: num(p.memory ?? p.memory_usage_pct ?? p.mem_pct),
    }));
  }

  return [];
}
